// Type-only on purpose: this module is imported by unit tests that mock very little, and pulling
// a runtime value out of @fs/zion-research-help would drag its whole dependency graph in with it.
import type { HintCategory } from '@fs/zion-research-help'

/**
 * Data problems that block temple work. Mirrors tree-data's ReservationsDataProblemsFilter
 * (ws/controller/reservations), which is the service-side source of truth for which suggestions
 * stop a person from being reserved. A data problem in this list ranks with possible duplicates;
 * everything else is an ordinary data problem.
 *
 * Keep in sync with that filter — tree-data does not expose the flag on the suggestion payload.
 */
const TEMPLE_BLOCKING_SUGGESTION_TYPES = new Set([
  // Person suggestions
  'BORN_AFTER_DIED',
  'CHRISTENED_AFTER_DIED',
  'LIVED_TOO_LONG',
  'UNKNOWN_GENDER',
  'BORN_AFTER_FATHER_DIED',
  'BORN_AFTER_MOTHER_COULD_BEAR_CHILDREN',
  'BORN_AFTER_MOTHER_DIED',
  'BORN_BEFORE_FATHER_COULD_FATHER_CHILDREN',
  'BORN_BEFORE_MOTHER_COULD_BEAR_CHILDREN',
  'BORN_BEFORE_FATHER',
  'BORN_BEFORE_MOTHER',
  'CHRISTENED_BEFORE_FATHER_BORN',
  'CHRISTENED_BEFORE_FATHER_COULD_FATHER_CHILDREN',
  'CHRISTENED_BEFORE_MOTHER_BORN',
  'CHRISTENED_BEFORE_MOTHER_COULD_BEAR_CHILDREN',
  'DIED_BEFORE_FATHER_BORN',
  'DIED_BEFORE_MOTHER_BORN',
  // Couple suggestions
  'BORN_AFTER_SPOUSE_DIED',
  'BORN_AFTER_MARRIED',
  'DIED_BEFORE_MARRIED',
  'DIED_BEFORE_SPOUSE_BORN',
])

export function preventsTempleWork(suggestion: { type?: { name?: string } }): boolean {
  return TEMPLE_BLOCKING_SUGGESTION_TYPES.has(suggestion.type?.name ?? '')
}

/**
 * Per-category hint counts for one person. Keyed by `@fs/zion-research-help`'s HintCategory so the
 * counts can be handed straight to its getTopHintCategory — the priority order and the icon artwork
 * both live in that package, not here.
 */
export type HintCounts = Record<HintCategory, number>

export const EMPTY_HINT_COUNTS: HintCounts = {
  'possible-duplicates': 0,
  'potential-ancestors': 0,
  'data-problems': 0,
  'record-hints': 0,
  'myheritage-matches': 0,
  'research-suggestions': 0,
}

export function getTotalHintCount(counts: HintCounts): number {
  return Object.values(counts).reduce((total, count) => total + count, 0)
}
